/**
 * On-Call Management - Constants
 */

import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// ============ STATUS OPTIONS ============

export const ASSIGNMENT_STATUS = [
    { label: 'scheduled', value: 'scheduled' },
    { label: 'active', value: 'active' },
    { label: 'completed', value: 'completed' },
    { label: 'reassigned', value: 'reassigned' },
    { label: 'swapped', value: 'swapped' },
]

export const ASSIGNMENT_SOURCE_OPTIONS = [
    { label: 'auto', value: 'auto' },
    { label: 'manual', value: 'manual' },
]

export const SWAP_STATUS = [
    { label: 'pending', value: 'pending' },
    { label: 'accepted', value: 'accepted' },
    { label: 'rejected', value: 'rejected' },
    { label: 'approved', value: 'approved' },
    { label: 'cancelled', value: 'cancelled' },
]

export const COMPENSATION_STATUS = [
    { label: 'pending', value: 'pending' },
    { label: 'approved', value: 'approved' },
    { label: 'rejected', value: 'rejected' },
    { label: 'paid', value: 'paid' },
]

// ============ ROTATION FREQUENCY OPTIONS ============

export const ROTATION_FREQUENCY_OPTIONS = [
    { label: 'daily', value: 'daily', days: 1 },
    { label: 'weekly', value: 'weekly', days: 7 },
    { label: 'bi_weekly', value: 'bi_weekly', days: 14 },
    { label: 'monthly', value: 'monthly', days: 30 },
]

// ============ COMPENSATION RATES ============

export const COMPENSATION_RATES = {
    weekday: 1.0,
    weekend: 1.5,
    holiday: 2.0,
} as const

export const getCompensationRateLabel = (rate: number): string => {
    switch (rate) {
        case 1.0:
            return 'weekday'
        case 1.5:
            return 'weekend'
        case 2.0:
            return 'holiday'
        default:
            return `${rate}x`
    }
}

// ============ LOG ACTIONS ============

export const LOG_ACTIONS = {
    SCHEDULE_CREATED: 'SCHEDULE_CREATED',
    SCHEDULE_UPDATED: 'SCHEDULE_UPDATED',
    SCHEDULE_DELETED: 'SCHEDULE_DELETED',
    MEMBER_ADDED: 'MEMBER_ADDED',
    MEMBER_REMOVED: 'MEMBER_REMOVED',
    MEMBERS_REORDERED: 'MEMBERS_REORDERED',
    ASSIGNMENT_CREATED: 'ASSIGNMENT_CREATED',
    ASSIGNMENT_REASSIGNED: 'ASSIGNMENT_REASSIGNED',
    SWAP_REQUESTED: 'SWAP_REQUESTED',
    SWAP_ACCEPTED: 'SWAP_ACCEPTED',
    SWAP_REJECTED: 'SWAP_REJECTED',
    SWAP_APPROVED: 'SWAP_APPROVED',
    SWAP_CANCELLED: 'SWAP_CANCELLED',
    COMPENSATION_APPROVED: 'COMPENSATION_APPROVED',
    COMPENSATION_REJECTED: 'COMPENSATION_REJECTED',
    COMPENSATION_PAID: 'COMPENSATION_PAID',
} as const

// ============ ASSIGNMENT SOURCE ============

export type AssignmentSource = 'system' | 'manual'

export const getAssignmentSource = (
    createdBy: number | null
): AssignmentSource => {
    return createdBy === null ? 'system' : 'manual'
}

// ============ COLOR PALETTE ============
// Aligned with Tailwind CSS color palette for consistency
// Used for inline styles where Tailwind classes can't be applied dynamically

const COLORS = {
    // Status colors (Tailwind equivalents noted)
    teal600: 'rgb(13, 148, 136)', // teal-600 - system/auto
    indigo600: 'rgb(79, 70, 229)', // indigo-600 - manual
    amber500: 'rgb(245, 158, 11)', // amber-500 - pending/scheduled
    green700: 'rgb(21, 128, 61)', // green-700 - success
    red600: 'rgb(220, 38, 38)', // red-600 - error
    blue500: 'rgb(59, 130, 246)', // blue-500 - info
    purple600: 'rgb(147, 51, 234)', // purple-600 - special
} as const

const COLORS_BG = {
    teal: 'rgba(13, 148, 136, 0.1)',
    indigo: 'rgba(79, 70, 229, 0.1)',
    amber: 'rgba(245, 158, 11, 0.1)',
    green: 'rgba(21, 128, 61, 0.1)',
    red: 'rgba(220, 38, 38, 0.1)',
    blue: 'rgba(59, 130, 246, 0.1)',
    purple: 'rgba(147, 51, 234, 0.1)',
} as const

/**
 * Get text color for assignment source badge
 * @param source - 'system' (auto-generated) or 'manual' (user-created)
 */
export const getSourceColor = (source: AssignmentSource): string => {
    return source === 'system' ? COLORS.teal600 : COLORS.indigo600
}

/**
 * Get background color for assignment source badge
 * @param source - 'system' (auto-generated) or 'manual' (user-created)
 */
export const getSourceBgColor = (source: AssignmentSource): string => {
    return source === 'system' ? COLORS_BG.teal : COLORS_BG.indigo
}

// ============ STATUS COLORS ============

/**
 * Get text color for status badge
 * @param status - Status string (pending, scheduled, approved, etc.)
 */
export const getStatusColor = (status: string): string => {
    switch (status) {
        case 'pending':
        case 'scheduled':
            return COLORS.amber500
        case 'approved':
        case 'completed':
        case 'paid':
        case 'active':
            return COLORS.green700
        case 'rejected':
        case 'cancelled':
            return COLORS.red600
        case 'accepted':
            return COLORS.blue500
        case 'reassigned':
        case 'swapped':
            return COLORS.purple600
        default:
            return 'inherit'
    }
}

/**
 * Get background color for status badge
 * @param status - Status string (pending, scheduled, approved, etc.)
 */
export const getStatusBgColor = (status: string): string => {
    switch (status) {
        case 'pending':
        case 'scheduled':
            return COLORS_BG.amber
        case 'approved':
        case 'completed':
        case 'paid':
        case 'active':
            return COLORS_BG.green
        case 'rejected':
        case 'cancelled':
            return COLORS_BG.red
        case 'accepted':
            return COLORS_BG.blue
        case 'reassigned':
        case 'swapped':
            return COLORS_BG.purple
        default:
            return 'transparent'
    }
}

// ============ REACT QUERY KEYS ============
// Re-export from central query keys for type safety with useQueryWithHandler

export const ONCALL_QUERY_KEYS = {
    SCHEDULES: QUERY_KEYS.ONCALL_SCHEDULES,
    SCHEDULE_DETAIL: QUERY_KEYS.ONCALL_SCHEDULE_DETAIL,
    ROTATIONS: QUERY_KEYS.ONCALL_ROTATIONS,
    ROTATION_DETAIL: QUERY_KEYS.ONCALL_ROTATION_DETAIL,
    ROTATION_MEMBERS: QUERY_KEYS.ONCALL_ROTATION_MEMBERS,
    CYCLE_TEMPLATE: QUERY_KEYS.ONCALL_CYCLE_TEMPLATE,
    OVERRIDES: QUERY_KEYS.ONCALL_OVERRIDES,
    ASSIGNMENTS: QUERY_KEYS.ONCALL_ASSIGNMENTS,
    ASSIGNMENT_DETAIL: QUERY_KEYS.ONCALL_ASSIGNMENT_DETAIL,
    ASSIGNMENTS_CURRENT: QUERY_KEYS.ONCALL_ASSIGNMENTS_CURRENT,
    ASSIGNMENTS_TODAY: QUERY_KEYS.ONCALL_ASSIGNMENTS_TODAY,
    ASSIGNMENTS_MY: QUERY_KEYS.ONCALL_ASSIGNMENTS_MY,
    SWAPS: QUERY_KEYS.ONCALL_SWAPS,
    SWAP_DETAIL: QUERY_KEYS.ONCALL_SWAP_DETAIL,
    SWAPS_PENDING: QUERY_KEYS.ONCALL_SWAPS_PENDING,
    SWAPS_MY: QUERY_KEYS.ONCALL_SWAPS_MY,
    COMPENSATIONS: QUERY_KEYS.ONCALL_COMPENSATIONS,
    COMPENSATION_DETAIL: QUERY_KEYS.ONCALL_COMPENSATION_DETAIL,
    COMPENSATION_REPORT: QUERY_KEYS.ONCALL_COMPENSATION_REPORT,
    COMPENSATIONS_MY: QUERY_KEYS.ONCALL_COMPENSATIONS_MY,
    LOGS: QUERY_KEYS.ONCALL_LOGS,
} as const

// ============ CACHE INVALIDATION HOOK ============

export const useRefetchOncallQueries = () => {
    const queryClient = useQueryClient()

    const refetchSchedules = async () => {
        await queryClient.invalidateQueries({
            queryKey: [ONCALL_QUERY_KEYS.SCHEDULES],
        })
    }

    const refetchAssignments = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.ASSIGNMENTS],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.ASSIGNMENTS_CURRENT],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.ASSIGNMENTS_TODAY],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.ASSIGNMENTS_MY],
            }),
        ])
    }

    const refetchSwaps = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.SWAPS],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.SWAPS_PENDING],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.SWAPS_MY],
            }),
        ])
    }

    const refetchCompensations = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.COMPENSATIONS],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.COMPENSATION_REPORT],
            }),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.COMPENSATIONS_MY],
            }),
        ])
    }

    const refetchAll = async () => {
        await Promise.all([
            refetchSchedules(),
            refetchAssignments(),
            refetchSwaps(),
            refetchCompensations(),
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.LOGS],
            }),
        ])
    }

    return {
        refetchSchedules,
        refetchAssignments,
        refetchSwaps,
        refetchCompensations,
        refetchAll,
    }
}

// ============ DEFAULT VALUES ============

export const DEFAULT_SHIFT_START = '08:00'
export const DEFAULT_SHIFT_END = '08:00' // Next day 8 AM = 24 hour shift
export const DEFAULT_PAGE_SIZE = 10

// ============ TIME FORMATTING ============

/**
 * Format time value from various formats to HH:mm format
 * Handles:
 * - ISO datetime strings: "2025-12-18T08:00:00" -> "08:00"
 * - Time strings: "08:00:00" -> "08:00"
 * - pgtype.Time objects: { Microseconds: number, Valid: boolean }
 */
export const formatTimeValue = (time: unknown): string => {
    if (!time) return '--:--'
    if (typeof time === 'string') {
        // Check if it's an ISO datetime string (contains 'T')
        if (time.includes('T')) {
            const timePart = time.split('T')[1]
            return timePart ? timePart.slice(0, 5) : '--:--'
        }
        // Regular time string like "08:00:00"
        return time.slice(0, 5)
    }
    if (typeof time === 'object' && time !== null) {
        const t = time as {
            Microseconds?: number
            Valid?: boolean
            microseconds?: number
            valid?: boolean
        }
        // Handle pgtype.Time format { Microseconds: number, Valid: boolean }
        const microseconds = t.Microseconds ?? t.microseconds
        if (microseconds !== undefined) {
            const totalSeconds = Math.floor(microseconds / 1000000)
            const hours = Math.floor(totalSeconds / 3600)
            const minutes = Math.floor((totalSeconds % 3600) / 60)
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
        }
    }
    return '--:--'
}
